/**
 * Instant Booking Preferences Types
 * These types define the structure for instant booking configuration
 * at both tenant-wide and per-form levels
 */

/**
 * Schedule fill strategy - determines how to fill empty schedules
 */
export type ScheduleFillStrategy = 'earliest' | 'noon' | 'custom' | 'end_of_day';

/**
 * Round robin mode - determines assignment distribution
 *
 * - `strict` / `equal_rotation`: distribute appointments evenly, one after another
 * - `proximity_based`: legacy value (kept for backward compatibility with older saved data)
 * - `territories`: route appointments to specific team members based on zip code territories
 */
export type RoundRobinMode = 'strict' | 'proximity_based' | 'territories';

/**
 * Top choice strategy - determines how to prioritize slot suggestions
 */
export type TopChoiceStrategy = 'customer_preference' | 'company_benefit' | 'intelligent';

/**
 * Territory mode - how strictly zip code rules are enforced
 */
export type TerritoryMode = 'exclusive' | 'preferred';

/**
 * Capacity overflow action - what to do when all team members hit daily max
 */
export type CapacityOverflowAction = 'deny' | 'submit_request' | 'next_day';

/**
 * Regional assignment for a specific user.
 *
 * `territories` is the source of truth for which territories (defined in
 * Route Preferences) the user is subscribed to. `zipCodes` is a denormalized
 * cache derived from those territories at save time so the booking engine
 * (`InstantBookingPreferencesManager.isUserEligibleForRegion`) can keep
 * matching against a flat list. Whenever Route Preferences updates a
 * territory's zip set, Smart Assignment recomputes `zipCodes` on the next
 * load and re-saves so subscribers stay in sync automatically.
 */
export interface RegionalAssignment {
  userId: string;
  userName?: string; // For display purposes
  /** Territory IDs the user is subscribed to (source of truth). */
  territories?: string[];
  /** Denormalized zip codes derived from `territories` at save time. */
  zipCodes?: string[];
  regions?: string[]; // City/region names
}

/**
 * A single teammate's share of the Equal Rotation cycle.
 *
 * `weight` is a relative share, not a percentage — a team on `[3, 1]` sends
 * three of every four bookings to the first teammate. `1` for everyone is a
 * plain even split (the default), and `0` pauses a teammate without removing
 * them from the event.
 */
export interface RotationWeight {
  userId: string;
  userName?: string; // For display purposes
  weight: number;
}

/**
 * Intelligent weighting configuration for top choice calculation
 */
export interface IntelligentWeighting {
  proximityWeight: number; // 0-100
  customerPreferenceWeight: number; // 0-100
}

/**
 * Complete instant booking preferences configuration
 */
export interface InstantBookingPreferences {
  // Schedule Fill Preferences
  scheduleFillStrategy: ScheduleFillStrategy;
  customScheduleFillTime?: string; // e.g., "13:00" for 1pm
  
  // Round Robin Rules
  roundRobinMode: RoundRobinMode;

  // Proximity Override (Equal Rotation mode):
  // When enabled, if a teammate has an existing appointment within proximityThresholdMiles
  // of the new appointment's address, the Proximity slot is produced from that teammate
  // (instead of the rotation user) to reduce drive time. Company and Customer slots still
  // come from the rotation user. Rotation is preserved — the skipped user stays next up.
  enableProximityOverride: boolean;
  proximityThresholdMiles: number;

  // Equal Rotation Override Rules
  // When mode === 'strict' (Equal Rotation): if the customer's preferred date
  // or time isn't available for the next teammate in the rotation, render slots
  // from any other available teammate so the customer's preference can still
  // be met (instead of losing the booking).
  equalRotationCustomerPreferenceOverrideEnabled: boolean;

  // Rotation Balance (Equal Rotation mode)
  // Off by default, which means every teammate takes an equal turn. When on,
  // each teammate carries a relative `weight` and the rotation is dealt out in
  // proportion to those weights instead of one-for-one. Order stays interleaved
  // (a 3:1 split reads A, A, B, A — not A, A, A, B) so no one waits out a long
  // streak, and the override rules below still apply on top.
  enableRotationBalance: boolean;
  rotationWeights: RotationWeight[];

  // Regional Assignments (territories mode)
  enableRegionalAssignments: boolean;
  regionalAssignments: RegionalAssignment[];
  territoryMode: TerritoryMode;

  // Rotation Balance (Territories mode)
  // When two or more teammates subscribe to the same territory, bookings in it
  // round-robin between them. This weights that per-territory rotation the same
  // way `rotationWeights` weights the tenant-wide one — keyed by territory id,
  // because a teammate can carry a different share in each territory they share.
  enableTerritoryRotationBalance: boolean;
  territoryRotationWeights: Record<string, RotationWeight[]>;

  // Territories Override Rules
  // When mode === 'territories': if the customer's preferred date/time is not
  // available for the territory's normally-assigned teammate, render slots from
  // any other available teammate so the customer's preference can still be met.
  territoryCustomerPreferenceOverrideEnabled: boolean;
  
  // Daily Capacity
  defaultMaxAppointmentsPerDay: number;
  
  // Weekly Rebalancing
  enableWeeklyRebalancing: boolean;
  
  // Overflow Behavior
  capacityOverflowAction: CapacityOverflowAction;
  
  // Top Choice Logic
  topChoiceStrategy: TopChoiceStrategy;
  intelligentWeighting?: IntelligentWeighting;
}

/**
 * Default instant booking preferences
 */
export const DEFAULT_INSTANT_BOOKING_PREFERENCES: InstantBookingPreferences = {
  scheduleFillStrategy: 'earliest',
  customScheduleFillTime: '13:00',
  roundRobinMode: 'strict',
  proximityThresholdMiles: 15,
  enableProximityOverride: false,
  equalRotationCustomerPreferenceOverrideEnabled: false,
  enableRotationBalance: false,
  rotationWeights: [],
  enableRegionalAssignments: false,
  regionalAssignments: [],
  territoryMode: 'exclusive',
  enableTerritoryRotationBalance: false,
  territoryRotationWeights: {},
  territoryCustomerPreferenceOverrideEnabled: false,
  defaultMaxAppointmentsPerDay: 5,
  enableWeeklyRebalancing: false,
  capacityOverflowAction: 'submit_request',
  topChoiceStrategy: 'customer_preference',
  intelligentWeighting: {
    proximityWeight: 50,
    customerPreferenceWeight: 50
  }
};

/**
 * Instant booking preferences with override flag for form-level configuration
 */
export interface FormInstantBookingPreferences extends InstantBookingPreferences {
  overrideTenantDefaults: boolean;
}

/**
 * DTO for saving/loading instant booking preferences
 * This will be mapped to backend API when available
 */
export interface InstantBookingPreferencesDto {
  // Schedule Fill Preferences
  scheduleFillStrategy?: string;
  customScheduleFillTime?: string | null;
  
  // Round Robin Rules
  roundRobinMode?: string;
  proximityThresholdMiles?: number | null;
  enableProximityOverride?: boolean | null;
  
  // Regional Assignments
  enableRegionalAssignments?: boolean | null;
  regionalAssignmentsJson?: string | null; // JSON serialized RegionalAssignment[]
  territoryMode?: string | null;
  
  // Daily Capacity & Rebalancing
  defaultMaxAppointmentsPerDay?: number | null;
  enableWeeklyRebalancing?: boolean | null;
  capacityOverflowAction?: string | null;
  
  // Top Choice Logic
  topChoiceStrategy?: string;
  intelligentWeightingJson?: string | null; // JSON serialized IntelligentWeighting
}

